import { createContext, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { lockScroll } from "../lib/scrollLock";
import { BUSINESS, TRIP_TYPES, waLink } from "../data/packages";

export const EASE = [0.16, 1, 0.3, 1];

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

export function EnquiryProvider({ children }) {
  const [preset, setPreset] = useState(null);
  const openEnquiry = (opts = {}) =>
    setPreset({
      tripType: opts.tripType || "",
      destination: opts.destination || "",
    });
  const closeEnquiry = () => setPreset(null);

  return (
    <EnquiryContext.Provider value={{ openEnquiry, closeEnquiry }}>
      {children}
      <AnimatePresence>
        {preset && <EnquiryModal preset={preset} onClose={closeEnquiry} />}
      </AnimatePresence>
    </EnquiryContext.Provider>
  );
}

const fieldCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-aurora/60 focus:ring-2 focus:ring-aurora/20";

function EnquiryModal({ preset, onClose }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    tripType: preset.tripType || "Domestic Package",
    destination: preset.destination || "",
    travelDate: "",
    groupSize: "",
    message: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => lockScroll(), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        trip_type: form.tripType,
        destination: form.destination || null,
        travel_date: form.travelDate || null,
        group_size: form.groupSize ? Number(form.groupSize) : null,
        message: form.message || null,
        source: "enquiry-modal",
      });
      toast.success("Enquiry received! Our route curator will reach out within 24 hours.");
      onClose();
    } catch (err) {
      toast.error("Could not send your enquiry. Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      data-testid="enquiry-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[130] flex items-end justify-center p-0 sm:items-center sm:p-6"
    >
      <div className="absolute inset-0 bg-ink/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="relative z-10 max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-white/10 bg-obsidian p-7 shadow-2xl sm:rounded-3xl md:p-9"
      >
        <button
          data-testid="enquiry-modal-close-btn"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-5 top-5 rounded-full border border-white/10 p-2 text-slate-400 transition-colors hover:border-aurora/50 hover:text-aurora"
        >
          <X className="h-4 w-4" />
        </button>

        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Start Your Journey</p>
        <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-50">
          Tell us where you want to <em className="font-editorial font-medium italic text-comet">belong</em>
        </h3>
        <p className="mt-2 text-sm text-slate-400">Our curators reply within 24 hours with a draft route.</p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="enquiry-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <input required minLength={2} data-testid="enquiry-name-input" className={fieldCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
            <input required type="email" data-testid="enquiry-email-input" className={fieldCls} placeholder="Email *" value={form.email} onChange={set("email")} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <input required type="tel" minLength={6} data-testid="enquiry-phone-input" className={fieldCls} placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} />
            <select data-testid="enquiry-trip-type-select" className={`${fieldCls} [&>option]:bg-obsidian`} value={form.tripType} onChange={set("tripType")}>
              {TRIP_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="enquiry-destination-input" className={fieldCls} placeholder="Destination / trip name" value={form.destination} onChange={set("destination")} />
            <input data-testid="enquiry-date-input" className={fieldCls} placeholder="Tentative dates (e.g. Apr 2026)" value={form.travelDate} onChange={set("travelDate")} />
          </div>
          <input type="number" min="1" data-testid="enquiry-group-size-input" className={fieldCls} placeholder="Number of travellers" value={form.groupSize} onChange={set("groupSize")} />
          <textarea rows={3} data-testid="enquiry-message-input" className={`${fieldCls} resize-none`} placeholder="Anything we should know — pace, budget, celebrations, must-sees…" value={form.message} onChange={set("message")} />
          <button
            type="submit"
            disabled={busy}
            data-testid="enquiry-submit-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-aurora py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,245,212,0.4)] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {busy ? "Sending…" : "Send Enquiry"}
          </button>
        </form>
        <p className="mt-5 text-center text-xs text-slate-400">
          Prefer WhatsApp?{" "}
          <a
            data-testid="enquiry-modal-whatsapp-link"
            href={waLink(`Hi ${BUSINESS.name}! I want to plan a trip.`)}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-aurora hover:underline"
          >
            Message us directly on WhatsApp
          </a>
        </p>
      </motion.div>
    </motion.div>
  );
}
